import { format, isValid, parse } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Formats a date to DD/MM/YYYY format
 */
export function formatDateAR(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  if (!isValid(dateObj)) return "";
  return format(dateObj, "dd/MM/yyyy");
}

/**
 * Formats a date to DD/MM/YYYY HH:mm format
 */
export function formatDateTimeAR(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  if (!isValid(dateObj)) return "";
  return format(dateObj, "dd/MM/yyyy HH:mm");
}

/**
 * Formats a date using locale-specific formatting
 */
export function formatDateLocale(
  date: Date | string | number,
  localeFormat = "dd/MM/yyyy"
): string {
  if (!date) return "";
  const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  if (!isValid(dateObj)) return "";
  return format(dateObj, localeFormat, { locale: es });
}

/**
 * Formats validity date from DD/MM/YYYY format to "DD MMM" format
 * Handles both DD/MM/YYYY and ISO date formats
 */
export function formatValidityDate(dateString?: string): string | null {
  if (!dateString) return null;

  // Primary path: Backend sends DD/MM/YYYY format
  if (dateString.includes("/")) {
    const parts = dateString.split("/");
    if (parts.length !== 3) return null; // Ensure we have day/month/year

    const [day, month] = parts;
    const monthNames = [
      "ene", "feb", "mar", "abr", "may", "jun",
      "jul", "ago", "sep", "oct", "nov", "dic"
    ];

    const monthIndex = parseInt(month) - 1;
    // Validate month is within valid range (0-11)
    if (monthIndex < 0 || monthIndex > 11) return null;

    return `${day} ${monthNames[monthIndex]}`;
  }

  // Fallback: Try parsing ISO format if it comes directly
  const date = new Date(dateString);
  if (!isValid(date)) return null;

  return format(date, "d MMM", { locale: es });
}

/**
 * Parses a DD/MM/YYYY date string and returns a Date object
 */
export function parseDateAR(dateString: string): Date | null {
  if (!dateString) return null;

  try {
    const parsed = parse(dateString, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
